import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  Message as MessageIcon,
  PushPin as PinIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useAxios } from '../context/AxiosContext';

const SuperuserAdmin = () => {
  const { api, user, fetchUser } = useAxios();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverStats, setServerStats] = useState([]);
  const [overallStats, setOverallStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAdminStats = async () => {
    try {
      setRefreshing(true);
      const response = await api.get('/discord/admin/server-statistics/');
      setServerStats(response.data.servers || []);
      setOverallStats(response.data.overall || {});
      setError(null);
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setError(err.response?.data?.error || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Only fetch data if user is superuser
    if (user?.is_superuser) {
      fetchAdminStats();
    } else {
      setLoading(false);
    }
  }, [user?.is_superuser]);

  // Debug: Log user data to console
  console.log('🔍 SuperuserAdmin - User data:', user);
  console.log('🔍 SuperuserAdmin - Is superuser?', user?.is_superuser);

  // Check if user is superuser after hooks are called
  if (!user?.is_superuser) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Access denied. This page is only available to superusers.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Current user data: {JSON.stringify(user, null, 2)}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => fetchUser()}
              startIcon={<RefreshIcon />}
            >
              Refresh Profile Data
            </Button>
          </Box>
        </Alert>
      </Container>
    );
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  const getLoadColor = (load) => {
    if (load >= 80) return 'error';
    if (load >= 60) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading admin statistics...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            🔧 Superuser Admin Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Monitor server load, statistics, and system health
          </Typography>
        </Box>
        <Tooltip title="Refresh Statistics">
          <IconButton 
            onClick={fetchAdminStats} 
            disabled={refreshing}
            sx={{ 
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Overall Statistics Cards */}
      {overallStats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <StorageIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4">{formatNumber(overallStats.totalServers)}</Typography>
                    <Typography variant="body2">Total Servers</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <MessageIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4">{formatNumber(overallStats.totalMessages)}</Typography>
                    <Typography variant="body2">Total Messages</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <GroupIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4">{formatNumber(overallStats.totalChannels)}</Typography>
                    <Typography variant="body2">Active Channels</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SpeedIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4">{overallStats.avgLoadPercentage}%</Typography>
                    <Typography variant="body2">Avg Load</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Server Statistics Table */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon />
            Server Load & Statistics
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {refreshing && <LinearProgress sx={{ mb: 2 }} />}
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: 'primary.main',
                  '& .MuiTableCell-root': {
                    color: 'primary.contrastText',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }
                }}>
                  <TableCell>Server</TableCell>
                  <TableCell align="center">Members</TableCell>
                  <TableCell align="center">Channels</TableCell>
                  <TableCell align="center">Messages</TableCell>
                  <TableCell align="center">24h</TableCell>
                  <TableCell align="center">7d</TableCell>
                  <TableCell align="center">Pinned</TableCell>
                  <TableCell align="center">Iframe 24h</TableCell>
                  <TableCell align="center">Iframe 7d</TableCell>
                  <TableCell align="center">Iframe Total</TableCell>
                  <TableCell align="center">Load %</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Iframe URL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serverStats.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No server statistics available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  serverStats.map((server) => (
                    <TableRow 
                      key={server.serverId}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: 'action.hover',
                          '& .MuiTableCell-root': {
                            color: 'text.primary'
                          }
                        },
                        ...(server.loadPercentage >= 80 && { 
                          bgcolor: 'error.light', 
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.main',
                            '& .MuiTableCell-root': {
                              color: 'error.contrastText'
                            }
                          }
                        })
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {server.icon ? (
                            <Avatar src={server.icon} sx={{ width: 32, height: 32 }} />
                          ) : (
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                              {server.name?.charAt(0) || 'S'}
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {server.name || `Server ${server.serverId}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {server.serverId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={formatNumber(server.memberCount)} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {server.channelCount}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          {formatNumber(server.messageCount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="primary.main" fontWeight="medium">
                          {formatNumber(server.messages24h || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="secondary.main" fontWeight="medium">
                          {formatNumber(server.messages7d || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <PinIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {server.pinnedCount}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="info.main" fontWeight="medium">
                          {formatNumber(server.iframeLoads24h || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="warning.main" fontWeight="medium">
                          {formatNumber(server.iframeLoads7d || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          {formatNumber(server.iframeLoadsAllTime || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ minWidth: 80 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(server.loadPercentage, 100)}
                              color={getLoadColor(server.loadPercentage)}
                              sx={{ 
                                width: '100%', 
                                height: 8, 
                                borderRadius: 4,
                                bgcolor: 'grey.200'
                              }}
                            />
                            <Typography variant="caption" fontWeight="bold">
                              {server.loadPercentage}%
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={server.status || 'Active'}
                          color={
                            server.loadPercentage >= 90 ? 'error' :
                            server.loadPercentage >= 70 ? 'warning' : 'success'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {server.iframeUrl ? (
                          <Tooltip title={`Iframe embedded at: ${server.iframeUrl}`} arrow>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => window.open(server.iframeUrl, '_blank')}
                              sx={{ 
                                fontSize: '0.75rem',
                                minWidth: 'auto',
                                px: 1
                              }}
                            >
                              View Site
                            </Button>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No iframe usage detected
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Top Channels by Load */}
      {serverStats.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📊 Top Channels by Activity
            </Typography>
            <Grid container spacing={2}>
              {serverStats
                .flatMap(server => 
                  server.topChannels?.map(channel => ({
                    ...channel,
                    serverName: server.name,
                    serverId: server.serverId
                  })) || []
                )
                .sort((a, b) => b.messageCount - a.messageCount)
                .slice(0, 6)
                .map((channel, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`${channel.serverId}-${channel.id}`}>
                    <Card variant="outlined" sx={{ bgcolor: index < 3 ? 'warning.light' : 'background.paper' }}>
                      <CardContent sx={{ pb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                          #{channel.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {channel.serverName}
                        </Typography>
                        <Typography variant="h6" color="primary.main">
                          {formatNumber(channel.messageCount)} messages
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default SuperuserAdmin;